/*
  FRG INSTALLMENTS â€” dynamic button text

  The "Installments" section calculates the button text for every variant ("Pay as low as 467 EGP per month") and
  stores it on the button as data-frg-texts = {"<variant id>": "<text>"}. This script only picks the text for the
  selected variant; it does not calculate anything, so the section stays the single source of truth.

  - Works with the button that assets/frg-installments.js copies into <frg-installments-button>.
  - Follows variant changes ("variant:change" event and changes of the product form's variant id).
  - A variant without a text (no available installment, no fallback) hides the button.
  - Theme Editor: re-reads the section's template after the section is reloaded.

  It never touches the drawer or the button's click behaviour.
*/

const HOST_SELECTOR = 'frg-installments-button';
const TOGGLE_SELECTOR = '.frg-installments-button__toggle';
const TEMPLATE_SELECTOR = 'template[data-frg-installments-button]';
const VARIANT_INPUT_SELECTOR = 'form[action*="/cart/add"] [name="id"]';

const observed = new WeakSet();

/* Texts per variant id, read from the section's template (fresh after a Theme Editor reload). */
function readTexts() {
  const template = document.querySelector(TEMPLATE_SELECTOR);
  if (!template) return null;

  const button = template.content.querySelector(TOGGLE_SELECTOR);
  if (!button || !button.hasAttribute('data-frg-texts')) return null;

  try {
    return JSON.parse(button.getAttribute('data-frg-texts'));
  } catch (error) {
    return null;
  }
}

/* The nearest ancestor that contains this product's form, so other product forms on the page are ignored. */
function findScope(host) {
  let el = host.parentElement;
  while (el && el !== document.body) {
    if (el.querySelector(VARIANT_INPUT_SELECTOR)) return el;
    el = el.parentElement;
  }
  return document;
}

function currentVariantId(scope) {
  const input = scope.querySelector(VARIANT_INPUT_SELECTOR);
  if (input && input.value) return String(input.value);

  const fromUrl = new URLSearchParams(window.location.search).get('variant');
  return fromUrl ? String(fromUrl) : null;
}

function update(host, variantId) {
  const texts = readTexts();

  if (!texts) {
    /* Section missing/disabled, or nothing to show for any variant */
    host.hidden = true;
    return;
  }

  const id = variantId === undefined ? currentVariantId(findScope(host)) : variantId;
  const toggle = host.querySelector(TOGGLE_SELECTOR);

  /* No variant known yet: keep the text rendered by the section for the selected variant */
  if (id === null) {
    host.hidden = false;
    return;
  }

  const text = Object.prototype.hasOwnProperty.call(texts, id) ? texts[id] : null;

  if (!text) {
    host.hidden = true;
    return;
  }

  host.hidden = false;
  if (!toggle) return;

  /* Pill style: only the text span changes, the icons stay */
  const target = toggle.querySelector('.frg-installments-button__text') || toggle;
  if (target.textContent !== text) target.textContent = text;
}

function setup(host) {
  if (observed.has(host)) return;
  observed.add(host);

  /* frg-installments.js may copy the button in later, or again after a Theme Editor reload */
  new MutationObserver(() => update(host)).observe(host, { childList: true, subtree: true });
  update(host);
}

function hosts() {
  return Array.prototype.slice.call(document.querySelectorAll(HOST_SELECTOR));
}

function refreshAll() {
  hosts().forEach((host) => {
    setup(host);
    update(host);
  });
}

function hostsFor(target) {
  return hosts().filter((host) => {
    const scope = findScope(host);
    return scope === document || !(target instanceof Node) || scope.contains(target);
  });
}

/* Theme's own variant event (detail.variant is null for an unavailable combination). Capture phase so it is caught
   even when the theme dispatches it without bubbling. */
document.addEventListener(
  'variant:change',
  (event) => {
    const variant = event.detail ? event.detail.variant : undefined;
    hostsFor(event.target).forEach((host) => {
      if (variant === undefined) update(host);
      else update(host, variant ? String(variant.id) : 'none');
    });
  },
  true
);

/* Fallback for themes that only update the form: re-read the variant id after any change in a product form */
document.addEventListener(
  'change',
  (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('form')) return;
    setTimeout(() => hostsFor(event.target).forEach((host) => update(host)), 0);
  },
  true
);

/* Theme Editor: section reloaded (new settings / new template) or product info re-rendered */
['shopify:section:load', 'shopify:section:unload'].forEach((name) => {
  document.addEventListener(name, () => setTimeout(refreshAll, 0));
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', refreshAll);
} else {
  refreshAll();
}